import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Search, Check, User } from "lucide-react";
import { customerApi, Customer } from "@/api/customer.api";
import { useDebounce } from "@/hooks/useDebounce";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

interface CustomerComboboxProps {
  value: string;
  onChange: (customerId: string) => void;
  error?: string;
}

export function CustomerCombobox({ value, onChange, error }: CustomerComboboxProps) {
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<Customer | null>(null);
  const [open, setOpen] = useState(false);
  const debouncedQuery = useDebounce(query, 300);

  const { data, isLoading } = useQuery({
    queryKey: ["customers", "combobox", debouncedQuery],
    queryFn: () => customerApi.list({ page: 1, limit: 8, search: debouncedQuery || undefined }),
    enabled: open,
  });

  return (
    <div className="relative">
      {selected ? (
        <div className="flex items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm">
          <div>
            <span className="font-medium">{selected.fullName}</span>{" "}
            <span className="font-data text-muted-foreground">· {selected.phone}</span>
          </div>
          <button
            type="button"
            className="text-xs text-primary hover:underline"
            onClick={() => {
              setSelected(null);
              onChange("");
            }}
          >
            Change
          </button>
        </div>
      ) : (
        <>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              className="pl-9"
              placeholder="Search customer by name or phone..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onFocus={() => setOpen(true)}
              onBlur={() => setTimeout(() => setOpen(false), 150)}
            />
          </div>
          {open && (
            <div className="absolute z-10 mt-1 max-h-56 w-full overflow-y-auto rounded-md border border-border bg-popover shadow-md">
              {isLoading ? (
                <p className="px-3 py-2 text-sm text-muted-foreground">Searching...</p>
              ) : !data || data.items.length === 0 ? (
                <p className="px-3 py-2 text-sm text-muted-foreground">
                  No customers found. Add them in the Customers module first.
                </p>
              ) : (
                data.items.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    className={cn(
                      "flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-muted",
                      value === c.id && "bg-muted"
                    )}
                    onMouseDown={() => {
                      setSelected(c);
                      onChange(c.id);
                      setOpen(false);
                    }}
                  >
                    <span className="flex items-center gap-2">
                      <User className="h-3.5 w-3.5 text-muted-foreground" />
                      {c.fullName}
                      <span className="font-data text-muted-foreground">{c.phone}</span>
                    </span>
                    {value === c.id && <Check className="h-3.5 w-3.5 text-primary" />}
                  </button>
                ))
              )}
            </div>
          )}
        </>
      )}
      {error && <p className="mt-1 text-xs text-destructive">{error}</p>}
    </div>
  );
}
